import {
  BoxGeometry,
  CanvasTexture,
  CapsuleGeometry,
  ConeGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PlaneGeometry,
  Sprite,
  SpriteMaterial,
  SphereGeometry,
  TorusGeometry,
  Vector3,
} from 'three';
import type { CharacterDefinition } from '../config/characterConfig';
import { COLORS } from '../config/colors';
import { CharacterState } from '../core/GameState';

const material = (color: number) =>
  new MeshStandardMaterial({ color, roughness: 0.72, flatShading: true });

const addShadow = (mesh: Mesh): Mesh => {
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
};

function createHelpSprite(): Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 320;
  canvas.height = 160;
  const context = canvas.getContext('2d')!;
  context.fillStyle = '#fff7e8';
  context.strokeStyle = '#172536';
  context.lineWidth = 12;
  context.beginPath();
  context.roundRect(10, 10, 300, 130, 24);
  context.fill();
  context.stroke();
  context.fillStyle = '#172536';
  context.font = '800 64px "Noto Sans SC Variable", system-ui';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText('救命！', 160, 76);
  const sprite = new Sprite(new SpriteMaterial({ map: new CanvasTexture(canvas), depthTest: false }));
  sprite.scale.set(4.8, 2.4, 1);
  sprite.position.set(0, 5.2, 0);
  sprite.visible = false;
  return sprite;
}

export class Character {
  readonly group = new Group();
  readonly definition: CharacterDefinition;
  state = CharacterState.IDLE;
  stamina: number;
  private elapsed = 0;
  private fallProgress = 0;
  private towingProgress = 0;
  private readonly leftArm = new Group();
  private readonly rightArm = new Group();
  private readonly leftLeg = new Group();
  private readonly rightLeg = new Group();
  private readonly head = new Group();
  private readonly helpSprite: Sprite;
  private readonly dazeStars = new Group();
  private readonly baseScale: number;
  private waterStart = new Vector3();

  constructor(definition: CharacterDefinition) {
    this.definition = definition;
    this.stamina = definition.stamina;
    this.baseScale = definition.kind === 'panda' ? 1.22 : definition.kind === 'duck' ? 0.72 : definition.animal ? 0.88 : 1;
    this.group.name = `Character_${definition.kind}`;
    if (definition.animal) this.createAnimal();
    else this.createHuman();
    this.helpSprite = createHelpSprite();
    this.group.add(this.helpSprite);
    this.createDazeStars();
    this.group.scale.setScalar(this.baseScale);
  }

  private createEyes(y: number, z: number): void {
    for (const x of [-0.34, 0.34]) {
      const eye = new Mesh(new SphereGeometry(0.11, 8, 6), new MeshBasicMaterial({ color: COLORS.ink }));
      eye.position.set(x, y, z);
      this.head.add(eye);
    }
  }

  private limb(group: Group, color: number, position: Vector3): void {
    const mesh = addShadow(new Mesh(new CapsuleGeometry(0.18, 0.86, 4, 8), material(color)));
    mesh.position.y = -0.62;
    group.add(mesh);
    group.position.copy(position);
    this.group.add(group);
  }

  private createHuman(): void {
    const skin = material(0xf5b98f);
    const torso = addShadow(new Mesh(new CapsuleGeometry(0.68, 1.45, 5, 10), material(this.definition.color)));
    torso.position.y = 1.75;
    this.group.add(torso);

    const headMesh = addShadow(new Mesh(new SphereGeometry(1.04, 14, 10), skin));
    headMesh.scale.y = 1.08;
    this.head.add(headMesh);
    this.head.position.y = 3.65;
    this.createEyes(0.16, 0.92);
    const nose = new Mesh(new ConeGeometry(0.1, 0.25, 7), skin);
    nose.rotation.x = Math.PI / 2;
    nose.position.set(0, -0.08, 1.05);
    this.head.add(nose);
    const hair = new Mesh(new SphereGeometry(1.06, 12, 7, 0, Math.PI * 2, 0, Math.PI / 2), material(0x4c342d));
    hair.position.y = 0.13;
    this.head.add(hair);
    this.group.add(this.head);

    this.limb(this.leftArm, this.definition.color, new Vector3(-0.83, 2.65, 0));
    this.limb(this.rightArm, this.definition.color, new Vector3(0.83, 2.65, 0));
    this.limb(this.leftLeg, 0x394864, new Vector3(-0.35, 1.05, 0));
    this.limb(this.rightLeg, 0x394864, new Vector3(0.35, 1.05, 0));

    if (['courier', 'tourist', 'photographer'].includes(this.definition.kind)) {
      const bag = addShadow(new Mesh(new BoxGeometry(1.2, 1.45, 0.55), material(0x6d4d3c)));
      bag.position.set(-0.9, 1.8, -0.38);
      bag.rotation.z = 0.12;
      this.group.add(bag);
    }
    if (this.definition.kind === 'raincoat') {
      const hood = new Mesh(new TorusGeometry(0.85, 0.22, 8, 16), material(0xffcf40));
      hood.position.set(0, 3.65, 0);
      this.group.add(hood);
    }
  }

  private createAnimal(): void {
    const bodyColor = this.definition.color;
    const body = addShadow(new Mesh(new CapsuleGeometry(0.8, 1.2, 5, 10), material(bodyColor)));
    body.rotation.z = Math.PI / 2;
    body.position.y = 1.45;
    this.group.add(body);

    const headMesh = addShadow(new Mesh(new SphereGeometry(1.15, 14, 10), material(bodyColor)));
    this.head.add(headMesh);
    this.head.position.set(0.75, 2.75, 0);
    this.createEyes(0.18, 1.02);

    if (this.definition.kind === 'duck') {
      const bill = new Mesh(new BoxGeometry(0.95, 0.28, 0.72), material(0xf09135));
      bill.position.set(0, -0.15, 1.08);
      this.head.add(bill);
    } else {
      const muzzle = new Mesh(new SphereGeometry(0.42, 10, 7), material(0xf0d2b0));
      muzzle.scale.y = 0.7;
      muzzle.position.set(0, -0.22, 0.99);
      this.head.add(muzzle);
    }

    if (['cat', 'orangeCat', 'dog', 'shiba', 'rabbit', 'panda'].includes(this.definition.kind)) {
      for (const x of [-0.64, 0.64]) {
        const ear = new Mesh(
          this.definition.kind === 'rabbit'
            ? new CapsuleGeometry(0.24, 1.05, 4, 8)
            : new ConeGeometry(0.45, 0.85, 7),
          material(bodyColor),
        );
        ear.position.set(x, 0.92, 0);
        this.head.add(ear);
      }
    }
    if (this.definition.kind === 'panda') {
      for (const x of [-0.38, 0.38]) {
        const patch = new Mesh(new SphereGeometry(0.25, 8, 6), material(0x20252a));
        patch.scale.set(1.2, 1.55, 0.35);
        patch.position.set(x, 0.14, 1.02);
        this.head.add(patch);
      }
    }
    this.group.add(this.head);

    const limbColor = this.definition.kind === 'panda' ? 0x24282d : bodyColor;
    this.limb(this.leftArm, limbColor, new Vector3(-0.2, 1.2, -0.62));
    this.limb(this.rightArm, limbColor, new Vector3(1.2, 1.2, -0.62));
    this.limb(this.leftLeg, limbColor, new Vector3(-0.2, 1.2, 0.62));
    this.limb(this.rightLeg, limbColor, new Vector3(1.2, 1.2, 0.62));

    if (!['duck', 'panda'].includes(this.definition.kind)) {
      const tail = new Mesh(new CapsuleGeometry(0.16, 0.8, 4, 8), material(bodyColor));
      tail.position.set(-1.15, 1.65, 0);
      tail.rotation.z = 1.2;
      this.group.add(tail);
    }
  }

  private createDazeStars(): void {
    for (let i = 0; i < 4; i += 1) {
      const star = new Mesh(new SphereGeometry(0.16, 6, 4), material(COLORS.warning));
      star.position.set(Math.cos(i * Math.PI / 2) * 1.3, 4.8, Math.sin(i * Math.PI / 2) * 1.3);
      this.dazeStars.add(star);
    }
    this.dazeStars.visible = false;
    this.group.add(this.dazeStars);
  }

  setState(state: CharacterState): void {
    this.state = state;
    this.elapsed = 0;
    this.helpSprite.visible = state === CharacterState.FALLING;
    this.dazeStars.visible = state === CharacterState.DAZED;
    if (state === CharacterState.FALLING) this.fallProgress = 0;
    if (state === CharacterState.BEING_TOWED) {
      this.towingProgress = 0;
      this.waterStart.copy(this.group.position);
    }
  }

  placeForWalk(): void {
    this.group.position.set(-22, 6.1, 5.5);
    this.group.rotation.set(0, Math.PI / 2, 0);
    this.setState(CharacterState.WALKING);
  }

  placeInWater(distance: number): void {
    this.group.position.set(-8, this.waterHeight, Math.max(-8.5, Math.min(8.5, 12 - distance)));
    this.group.rotation.set(0, 0, 0);
    this.setState(CharacterState.SWIMMING);
  }

  update(delta: number, riverSpeed: number, time: number): void {
    this.elapsed += delta;
    const wave = Math.sin(time * 4.2) * 0.14;
    if (this.state === CharacterState.WALKING) {
      this.group.position.z -= delta * 2.25;
      this.leftArm.rotation.x = Math.sin(time * 7) * 0.65;
      this.rightArm.rotation.x = -Math.sin(time * 7) * 0.65;
      this.leftLeg.rotation.x = -Math.sin(time * 7) * 0.6;
      this.rightLeg.rotation.x = Math.sin(time * 7) * 0.6;
    }
    if (this.state === CharacterState.FALLING) {
      this.fallProgress = Math.min(1, this.fallProgress + delta / 1.45);
      const p = this.fallProgress;
      this.group.position.x = -22 + p * 8;
      this.group.position.z = 0.1 - p * 0.5;
      this.group.position.y = 6.1 + Math.sin(p * Math.PI) * 3.2 - p * 5.65;
      this.group.rotation.z = Math.sin(p * Math.PI) * 1.5;
      this.leftArm.rotation.z = -2.2;
      this.rightArm.rotation.z = 2.2;
    }
    if (
      this.state === CharacterState.SWIMMING ||
      this.state === CharacterState.PANICKING ||
      this.state === CharacterState.DAZED ||
      this.state === CharacterState.TANGLED ||
      this.state === CharacterState.REACHING
    ) {
      this.group.position.x += delta * riverSpeed * this.definition.driftFactor * 1.45;
      this.group.position.y = this.waterHeight + wave;
      const panic = this.state === CharacterState.PANICKING ? 1.9 : this.state === CharacterState.DAZED ? 0.15 : 1;
      this.leftArm.rotation.z = -1.2 + Math.sin(time * 6 * panic) * 0.85;
      this.rightArm.rotation.z = 1.2 - Math.sin(time * 6 * panic) * 0.85;
      this.head.rotation.y = Math.sin(time * 1.8) * 0.28;
    }
    if (this.state === CharacterState.DAZED) {
      this.dazeStars.rotation.y += delta * 3.5;
    }
    if (this.state === CharacterState.BEING_TOWED) {
      this.towingProgress = Math.min(1, this.towingProgress + delta * 0.28);
      const target = new Vector3(this.waterStart.x + 6, 0.62, 13.8);
      this.group.position.lerpVectors(this.waterStart, target, this.towingProgress);
      this.group.rotation.z = Math.sin(time * 3) * 0.08;
    }
    if (this.state === CharacterState.SAFE) {
      this.group.position.y = 0.62 + Math.abs(Math.sin(time * 5.5)) * 0.32;
      this.group.rotation.y += delta * (this.definition.animal ? 2 : 0.4);
      this.leftArm.rotation.z = -2.4 + Math.sin(time * 7) * 0.25;
      this.rightArm.rotation.z = 2.4 - Math.sin(time * 7) * 0.25;
    }
    if (this.state === CharacterState.LOST_DOWNSTREAM) {
      this.group.position.x += delta * riverSpeed * 3.4;
      this.group.position.y = 0.18 + wave;
      this.group.rotation.y += delta * 0.45;
    }
  }

  get headPosition(): Vector3 {
    return this.group.position.clone().add(new Vector3(0, 2.4 * this.baseScale, 0));
  }

  private get waterHeight(): number {
    return this.definition.animal ? -1.28 : -1.82;
  }

  get isFallComplete(): boolean {
    return this.fallProgress >= 1;
  }

  get isTowComplete(): boolean {
    return this.towingProgress >= 1;
  }

  dispose(): void {
    this.group.traverse((object) => {
      if (object instanceof Mesh) {
        object.geometry.dispose();
        if (Array.isArray(object.material)) object.material.forEach((item) => item.dispose());
        else object.material.dispose();
      }
      if (object instanceof Sprite) {
        object.material.map?.dispose();
        object.material.dispose();
      }
    });
  }
}
